import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { 
  Bell, 
  Plus, 
  Clock, 
  Calendar,
  Trash2,
  Check,
  AlertTriangle,
  Repeat,
  X
} from "lucide-react";
import { format, formatDistanceToNow, isPast, isToday } from "date-fns";

interface Reminder {
  id: string;
  title: string;
  description: string | null;
  entity_type: string | null;
  entity_id: string | null;
  entity_title: string | null;
  reminder_date: string;
  frequency: string;
  status: string;
  priority: string;
  created_at: string;
}

export default function Reminders() {
  const { toast } = useToast();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newReminder, setNewReminder] = useState({
    title: "",
    description: "",
    reminderDate: "",
    reminderTime: "",
    frequency: "once",
    priority: "medium",
  });

  const { data: reminders, isLoading } = useQuery<Reminder[]>({
    queryKey: ["/api/reminders"],
  });

  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      return apiRequest("/api/reminders", {
        method: "POST",
        body: JSON.stringify(data),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/reminders"] });
      setIsCreateOpen(false);
      setNewReminder({
        title: "",
        description: "",
        reminderDate: "",
        reminderTime: "",
        frequency: "once",
        priority: "medium",
      });
      toast({
        title: "Reminder Created",
        description: "Your reminder has been scheduled.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create reminder",
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      return apiRequest(`/api/reminders/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/reminders"] });
      toast({
        title: "Reminder Updated",
        description: "Reminder status has been updated.",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest(`/api/reminders/${id}`, {
        method: "DELETE",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/reminders"] });
      toast({
        title: "Reminder Deleted",
        description: "The reminder has been removed.",
      });
    },
  });

  const handleCreate = () => {
    if (!newReminder.title || !newReminder.reminderDate) {
      toast({
        title: "Missing Fields",
        description: "Please provide a title and date.",
        variant: "destructive",
      });
      return;
    }

    const reminderDateTime = new Date(
      `${newReminder.reminderDate}T${newReminder.reminderTime || "09:00"}`
    );

    createMutation.mutate({
      title: newReminder.title,
      description: newReminder.description || null,
      reminderDate: reminderDateTime.toISOString(),
      frequency: newReminder.frequency,
      priority: newReminder.priority,
    });
  };

  const pendingReminders = reminders?.filter(r => r.status === "pending") || [];
  const completedReminders = reminders?.filter(r => r.status !== "pending") || [];

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "high": return "destructive";
      case "medium": return "default";
      case "low": return "secondary";
      default: return "outline";
    }
  };

  const getFrequencyLabel = (freq: string) => {
    const labels: Record<string, string> = {
      once: "One-time",
      daily: "Daily",
      weekly: "Weekly",
      monthly: "Monthly",
    };
    return labels[freq] || freq;
  };

  const ReminderCard = ({ reminder }: { reminder: Reminder }) => {
    const isOverdue = isPast(new Date(reminder.reminder_date)) && reminder.status === "pending";
    const isDueToday = isToday(new Date(reminder.reminder_date));

    return (
      <Card 
        className={`hover-elevate ${isOverdue ? "border-destructive/50" : isDueToday ? "border-warning/50" : ""}`}
        data-testid={`reminder-card-${reminder.id}`}
      >
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-medium truncate">{reminder.title}</h3>
                <Badge variant={getPriorityColor(reminder.priority) as any}>
                  {reminder.priority}
                </Badge>
                {reminder.frequency !== "once" && (
                  <Badge variant="outline" className="gap-1">
                    <Repeat className="w-3 h-3" />
                    {getFrequencyLabel(reminder.frequency)}
                  </Badge>
                )}
              </div>
              {reminder.description && (
                <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                  {reminder.description}
                </p>
              )}
              <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {format(new Date(reminder.reminder_date), "MMM d, yyyy")}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {format(new Date(reminder.reminder_date), "h:mm a")}
                </span>
                {isOverdue && (
                  <span className="flex items-center gap-1 text-destructive">
                    <AlertTriangle className="w-3 h-3" />
                    Overdue
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {reminder.status === "pending" && (
                <>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => updateMutation.mutate({ id: reminder.id, status: "dismissed" })}
                    data-testid={`button-dismiss-${reminder.id}`}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="text-green-600 hover:text-green-700"
                    onClick={() => updateMutation.mutate({ id: reminder.id, status: "sent" })}
                    data-testid={`button-complete-${reminder.id}`}
                  >
                    <Check className="w-4 h-4" />
                  </Button>
                </>
              )}
              <Button
                size="icon"
                variant="ghost"
                className="text-destructive hover:text-destructive"
                onClick={() => deleteMutation.mutate(reminder.id)}
                data-testid={`button-delete-${reminder.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="container mx-auto p-6 space-y-6" data-testid="page-reminders">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2" data-testid="title-reminders">
            <Bell className="w-8 h-8" />
            Reminders
          </h1>
          <p className="text-muted-foreground mt-1">
            Schedule notifications for your knowledge items
          </p>
        </div>
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button data-testid="button-create-reminder">
              <Plus className="w-4 h-4 mr-2" />
              New Reminder
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Reminder</DialogTitle>
              <DialogDescription>
                Set up a new reminder for yourself
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div>
                <label className="text-sm font-medium">Title</label>
                <Input
                  placeholder="Reminder title..."
                  value={newReminder.title}
                  onChange={(e) => setNewReminder({ ...newReminder, title: e.target.value })}
                  data-testid="input-reminder-title"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Description (optional)</label>
                <Textarea
                  placeholder="Add details..."
                  value={newReminder.description}
                  onChange={(e) => setNewReminder({ ...newReminder, description: e.target.value })}
                  data-testid="input-reminder-description"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Date</label>
                  <Input
                    type="date"
                    value={newReminder.reminderDate}
                    onChange={(e) => setNewReminder({ ...newReminder, reminderDate: e.target.value })}
                    data-testid="input-reminder-date"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Time</label>
                  <Input
                    type="time"
                    value={newReminder.reminderTime}
                    onChange={(e) => setNewReminder({ ...newReminder, reminderTime: e.target.value })}
                    data-testid="input-reminder-time"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Frequency</label>
                  <Select
                    value={newReminder.frequency}
                    onValueChange={(value) => setNewReminder({ ...newReminder, frequency: value })}
                  >
                    <SelectTrigger data-testid="select-frequency">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="once">One-time</SelectItem>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">Priority</label>
                  <Select
                    value={newReminder.priority}
                    onValueChange={(value) => setNewReminder({ ...newReminder, priority: value })}
                  >
                    <SelectTrigger data-testid="select-priority">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button 
                className="w-full" 
                onClick={handleCreate}
                disabled={createMutation.isPending}
                data-testid="button-submit-reminder"
              >
                {createMutation.isPending ? "Creating..." : "Create Reminder"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs defaultValue="pending" className="space-y-4">
        <TabsList>
          <TabsTrigger value="pending" data-testid="tab-pending">
            Pending ({pendingReminders.length})
          </TabsTrigger>
          <TabsTrigger value="completed" data-testid="tab-completed">
            Completed ({completedReminders.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="space-y-4">
          {isLoading ? (
            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <Card key={i}>
                  <CardContent className="p-4">
                    <Skeleton className="h-6 w-48 mb-2" />
                    <Skeleton className="h-4 w-full" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : pendingReminders.length > 0 ? (
            <div className="space-y-4">
              {pendingReminders.map((reminder) => (
                <ReminderCard key={reminder.id} reminder={reminder} />
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Bell className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="text-lg font-medium mb-2">No Pending Reminders</h3>
                <p className="text-muted-foreground mb-4">
                  Create a reminder to stay on top of your knowledge tasks
                </p>
                <Button onClick={() => setIsCreateOpen(true)} data-testid="button-create-empty">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Reminder
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="completed" className="space-y-4">
          {completedReminders.length > 0 ? (
            <div className="space-y-4">
              {completedReminders.map((reminder) => (
                <ReminderCard key={reminder.id} reminder={reminder} />
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Check className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="text-lg font-medium mb-2">No Completed Reminders</h3>
                <p className="text-muted-foreground">
                  Completed and dismissed reminders will appear here
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
